import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ConfirmModal from '@/components/common/ConfirmModal';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import CompleteExchangeModal from '@/features/listings/components/CompleteExchangeModal';
import {
    BUILDING_OWNER_LABELS,
    INSTITUTIONS,
    INSTITUTION_KEYS,
    institutionLabel,
} from '@/shared/constants/institutions';
import {
    BUILDING_MAX_LENGTH,
    CATEGORY_LABELS,
    OPEN_STATUSES,
    STATUS_LABELS,
    STATUS_VARIANTS,
} from '@/shared/constants/listings';
import listingService from '@/shared/services/listingService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { coordinatedInstitution } from '@/shared/utils/roles';
import { serverMessage } from '@/shared/utils/serverMessage';

// The route caps a page at 100 and pages with limit and offset, so a page is
// a read rather than a slice of a list the browser already holds.
const PAGE_SIZE = 25;

// Mirrors the `q` ceiling in listQuerySchema. Over it the route answers 400,
// which this tab would report as a connection problem, so the box cannot hold
// a term that would be refused.
const Q_MAX_LENGTH = 100;

// Browse defaults to the two open statuses. Moderation is the opposite job:
// a removed or expired listing is exactly what an admin came here to find, so
// an unset status filter names all six. The route admits `removed` on the
// cross-member path for an admin, and for a coordinator pinned to their own
// institution.
const ALL_STATUSES = Object.keys(STATUS_LABELS).join(',');

const EMPTY_FILTERS = Object.freeze({
    q: '',
    status: '',
    institution: '',
    category: '',
    building: '',
});

const EMPTY_FILTERED = 'No listing matches those filters. Try clearing one.';
const EMPTY_UNFILTERED = 'Nothing has been posted yet.';

// An empty box means "no filter", and the server rejects an empty string for
// institution, category and the search term, so blanks are dropped rather
// than sent. Status is the one exception: unset means every status.
//
// A coordinator's reads always name their institution. The server pins the
// moderation read itself; naming it here keeps a single-status filter, which
// the server answers as browse, on the same campus.
function toParams(filters, offset, scoped) {
    const params = {
        status: filters.status || ALL_STATUSES,
        limit: PAGE_SIZE,
        offset,
    };
    for (const key of ['institution', 'category', 'building', 'q']) {
        if (filters[key]) {
            params[key] = filters[key];
        }
    }
    if (scoped) {
        params.institution = scoped;
    }
    return params;
}

// A value stored on the server and missing from the client mirror renders as
// its raw key, so an enum added on one side only shows up in the table rather
// than blanking a cell out. The campus cell follows the same rule by hand,
// because InstitutionLogo renders nothing for a key it does not know.
const labelFor = (labels, key) => labels[key] || key;

const listingCount = (n) => `${n} ${n === 1 ? 'listing' : 'listings'}`;

/** A date as the reader's locale writes it, marked up as a time. */
function ShortDate({ value }) {
    return <time dateTime={value}>{new Date(value).toLocaleDateString()}</time>;
}

/**
 * Moderation names the poster. A report never does.
 *
 * The owner's name and department are columns here because judging a listing
 * means knowing whose it is and who to ask about it. The reports surface
 * carries no member identity at all, because a coordinator shows those
 * figures to a facilities director and who gave an item away is no part of
 * that case. Both rules are deliberate and they point opposite ways: the
 * reports projection must not be reused to build this table, and this table's
 * shape must not be reused in a report or an export.
 */
function ListingsTab() {
    const { userProfile } = useAuth();
    const scoped = coordinatedInstitution(userProfile);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    // Applied separately from the form state so typing in the search box does
    // not fire a request per keystroke; only a submit moves this.
    const [applied, setApplied] = useState(EMPTY_FILTERS);
    const [offset, setOffset] = useState(0);
    const [listings, setListings] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionError, setActionError] = useState('');
    const [pendingRemove, setPendingRemove] = useState(null);
    const [pendingComplete, setPendingComplete] = useState(null);

    const load = useCallback(
        async (active, from) => {
            setLoading(true);
            setError('');
            try {
                const data = await listingService.list(
                    toParams(active, from, scoped)
                );
                setListings(data.listings || []);
                setTotal(data.total || 0);
            } catch (_error) {
                setListings([]);
                setTotal(0);
                setError(
                    'Could not load the listings. Check your connection and try' +
                        ' again.'
                );
            } finally {
                setLoading(false);
            }
        },
        [scoped]
    );

    useEffect(() => {
        load(applied, offset);
    }, [applied, offset, load]);

    const handleRemove = async () => {
        const target = pendingRemove;
        setPendingRemove(null);
        setActionError('');
        try {
            await listingService.remove(target._id);
            // The page is reread rather than patched from the response. The
            // PATCH is answered from a plain findById, so its `owner` is a
            // bare id where the list read supplies a populated document, and
            // merging it over the row would blank the poster out of the one
            // column this tab exists to show. The reread also keeps `total`
            // and the other rows honest.
            await load(applied, offset);
        } catch (caught) {
            setActionError(
                serverMessage(
                    caught,
                    'Could not remove that listing. Try again.'
                )
            );
        }
    };

    // A claimed listing is the one an admin is asked about: the pair arranged
    // the handoff off the exchange and neither closed it out, so it sits
    // claimed past the term with nothing ageing it. The service admits an
    // admin as a party, so the only thing missing was the control.
    const handleComplete = async (outcome) => {
        const target = pendingComplete;
        setPendingComplete(null);
        setActionError('');
        try {
            await listingService.complete(target._id, outcome);
            // Reread for the reason a removal does: the response is answered
            // from a plain findById, so merging it over the row would blank
            // the poster out of the column this tab exists to show.
            await load(applied, offset);
        } catch (caught) {
            setActionError(
                serverMessage(
                    caught,
                    'Could not complete that exchange. Try again.'
                )
            );
        }
    };

    // Restore is relist. The transition a removed listing makes is the same
    // one an expired listing makes, down to the fresh expiry window and the
    // recomputed request count, and the route admits it for an admin alone.
    // It asks no confirmation because it is not destructive and Remove
    // reverses it, where a removal is what the member cannot undo.
    const handleRestore = async (target) => {
        setActionError('');
        try {
            await listingService.relist(target._id);
            // Reread for the same reason a removal does: the PATCH answers
            // from a plain findById, so its `owner` is a bare id and merging
            // it over the row would blank the poster out.
            await load(applied, offset);
        } catch (caught) {
            setActionError(
                serverMessage(
                    caught,
                    'Could not restore that listing. Try again.'
                )
            );
        }
    };

    const set = (key) => (event) =>
        setFilters({ ...filters, [key]: event.target.value });

    // A new filter starts at the first page, or a search run from page three
    // reads as empty when it has matches. The copy is what makes Search a
    // refresh as well: a moderation table has to pick up what another admin
    // changed, and passing the same object through would not re-read.
    const handleSearch = (event) => {
        event.preventDefault();
        setApplied({ ...filters });
        setOffset(0);
    };

    const filtered = Object.values(applied).some(Boolean);

    return (
        <>
            <h2 className="h4 mb-3">
                Listings{' '}
                <span className="text-muted fs-6">{listingCount(total)}</span>
            </h2>
            {scoped && (
                <p className="text-muted">
                    You moderate {institutionLabel(scoped)} listings: remove one
                    that should not be on offer, or restore one that was
                    removed.
                </p>
            )}
            <Form onSubmit={handleSearch} className="mb-3">
                <Row className="g-2 align-items-end">
                    <Col md={6} xl={3}>
                        <Form.Group controlId="admin-listings-q">
                            <Form.Label>Search listings</Form.Label>
                            <Form.Control
                                type="search"
                                maxLength={Q_MAX_LENGTH}
                                value={filters.q}
                                onChange={set('q')}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3} xl={2}>
                        <Form.Group controlId="admin-listings-status">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={filters.status}
                                onChange={set('status')}
                            >
                                <option value="">All statuses</option>
                                {Object.entries(STATUS_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    {!scoped && (
                        <Col md={3} xl={2}>
                            <Form.Group controlId="admin-listings-institution">
                                <Form.Label>Institution</Form.Label>
                                <Form.Select
                                    value={filters.institution}
                                    onChange={set('institution')}
                                >
                                    <option value="">All institutions</option>
                                    {INSTITUTION_KEYS.map((key) => (
                                        <option key={key} value={key}>
                                            {institutionLabel(key)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    )}
                    <Col md={4} xl={2}>
                        <Form.Group controlId="admin-listings-building">
                            <Form.Label>Building</Form.Label>
                            <Form.Control
                                type="search"
                                maxLength={BUILDING_MAX_LENGTH}
                                value={filters.building}
                                onChange={set('building')}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4} xl={2}>
                        <Form.Group controlId="admin-listings-category">
                            <Form.Label>Category</Form.Label>
                            <Form.Select
                                value={filters.category}
                                onChange={set('category')}
                            >
                                <option value="">All categories</option>
                                {Object.entries(CATEGORY_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={4} xl={1}>
                        <Button type="submit" className="w-100">
                            Search
                        </Button>
                    </Col>
                </Row>
            </Form>
            {error && <Alert variant="danger">{error}</Alert>}
            {actionError && <Alert variant="danger">{actionError}</Alert>}
            {loading && <LoadingSpinner message="Loading listings" />}
            {!loading && !error && (
                <>
                    {listings.length === 0 && offset === 0 && (
                        <p className="text-muted">
                            {filtered ? EMPTY_FILTERED : EMPTY_UNFILTERED}
                        </p>
                    )}
                    {listings.length > 0 && (
                        <Table hover responsive className="align-middle">
                            <thead>
                                <tr>
                                    <th scope="col">Title</th>
                                    <th scope="col">Campus</th>
                                    <th scope="col">Serviced by</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Posted by</th>
                                    <th scope="col">Dates</th>
                                    <th scope="col">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listings.map((item) => {
                                    // A poster's account can be gone while
                                    // their listing is not, so the row
                                    // survives an owner that did not
                                    // populate.
                                    const owner = item.owner || {};
                                    const open = OPEN_STATUSES.includes(
                                        item.status
                                    );
                                    // Completing an exchange is a party's act,
                                    // which the service grants an admin and
                                    // not a coordinator.
                                    const claimed =
                                        item.status === 'claimed' && !scoped;
                                    const actionable =
                                        open ||
                                        claimed ||
                                        item.status === 'removed';
                                    return (
                                        <tr key={item._id}>
                                            <td className="afe-col-title">
                                                <Link
                                                    to={`/listings/${item._id}`}
                                                >
                                                    {item.title}
                                                </Link>
                                                {/* Category and quantity ride
                                                    under the title rather than
                                                    in columns of their own, which
                                                    kept the table wider than a
                                                    laptop screen. */}
                                                <div className="small text-muted">
                                                    <span>
                                                        {labelFor(
                                                            CATEGORY_LABELS,
                                                            item.category
                                                        )}
                                                    </span>
                                                    {' · Qty '}
                                                    <span>{item.quantity}</span>
                                                </div>
                                            </td>
                                            <td className="afe-nowrap">
                                                {INSTITUTIONS[
                                                    item.institution
                                                ] ? (
                                                    <InstitutionLogo
                                                        institution={
                                                            item.institution
                                                        }
                                                    />
                                                ) : (
                                                    item.institution
                                                )}
                                            </td>
                                            {/* Short name in the cell, the full
                                                label (which says AHEC buildings
                                                are shared) on hover and for
                                                assistive technology. */}
                                            <td className="afe-nowrap">
                                                <abbr
                                                    title={labelFor(
                                                        BUILDING_OWNER_LABELS,
                                                        item.buildingOwner
                                                    )}
                                                    className="text-decoration-none"
                                                >
                                                    {institutionLabel(
                                                        item.buildingOwner
                                                    )}
                                                </abbr>
                                            </td>
                                            <td className="afe-nowrap">
                                                <Badge
                                                    bg={
                                                        STATUS_VARIANTS[
                                                            item.status
                                                        ]
                                                    }
                                                    className="afe-table-badge"
                                                >
                                                    {labelFor(
                                                        STATUS_LABELS,
                                                        item.status
                                                    )}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="afe-nowrap">
                                                    {owner.name ||
                                                        'Unknown member'}
                                                </div>
                                                <div className="text-muted small">
                                                    {owner.department}
                                                </div>
                                            </td>
                                            <td className="afe-nowrap small">
                                                <div>
                                                    Posted{' '}
                                                    <ShortDate
                                                        value={item.createdAt}
                                                    />
                                                </div>
                                                <div className="text-muted">
                                                    Expires{' '}
                                                    <ShortDate
                                                        value={item.expiresAt}
                                                    />
                                                </div>
                                            </td>
                                            <td>
                                                <div className="afe-actions flex-nowrap">
                                                    {open && (
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            aria-label={`Remove ${item.title}`}
                                                            onClick={() =>
                                                                setPendingRemove(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                    {claimed && (
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            aria-label={`Mark completed: ${item.title}`}
                                                            onClick={() =>
                                                                setPendingComplete(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            Mark completed
                                                        </Button>
                                                    )}
                                                    {item.status ===
                                                        'removed' && (
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            aria-label={`Restore ${item.title}`}
                                                            onClick={() =>
                                                                handleRestore(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            Restore
                                                        </Button>
                                                    )}
                                                    {!actionable && (
                                                        <span className="text-muted small">
                                                            No action
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                    {/* The pager outlives an empty page, because a result set
                        that shrank between reads leaves the admin past the
                        end and changing a filter resets the offset, so
                        dropping it here is a dead end. */}
                    {(listings.length > 0 || offset > 0) && (
                        <nav
                            aria-label="Listing pages"
                            className="d-flex align-items-center gap-3"
                        >
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={offset === 0}
                                onClick={() =>
                                    setOffset(Math.max(0, offset - PAGE_SIZE))
                                }
                            >
                                Previous page
                            </Button>
                            <span className="text-muted small">
                                {listings.length > 0
                                    ? `Showing ${offset + 1} to ` +
                                      `${offset + listings.length} of ` +
                                      listingCount(total)
                                    : 'Nothing on this page. ' +
                                      `${listingCount(total)} in all.`}
                            </span>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={
                                    listings.length === 0 ||
                                    offset + listings.length >= total
                                }
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                            >
                                Next page
                            </Button>
                        </nav>
                    )}
                </>
            )}
            <ConfirmModal
                show={Boolean(pendingRemove)}
                title="Remove this listing?"
                confirmLabel="Remove listing"
                cancelLabel="Keep listing"
                onCancel={() => setPendingRemove(null)}
                onConfirm={handleRemove}
            >
                <p className="mb-0">
                    {pendingRemove?.title} stops appearing in browse and answers
                    404 to everyone but its owner and its moderators. The owner
                    is emailed that it happened. Restore puts it back on offer
                    from this tab, for a fresh 30 days.
                </p>
            </ConfirmModal>
            <CompleteExchangeModal
                show={Boolean(pendingComplete)}
                listingTitle={pendingComplete?.title}
                onCancel={() => setPendingComplete(null)}
                onConfirm={handleComplete}
            />
        </>
    );
}

export default ListingsTab;
